// components/ofertas/OfertaTarifa.js
import React, { useEffect, useCallback } from "react";
import Layout from "../../hocs/Layout";
import { useNavigate, useParams } from "react-router-dom";
import { connect } from "react-redux";
import { Col, Form, Row, Card, Container } from "react-bootstrap";
import "react-datepicker/dist/react-datepicker.css";

import { TARIFAS_OFERTA } from "../../utils/constants/tarifas";
import { CoeficienteKInput } from "../../components/ofertas/OfertaCoeficienteKInput";
import { useInitialDataOffer } from "../../components/hooks/useInitialDataOffer";
import { useOfertaTarifaForm } from "../../components/hooks/useOfertaTarifaForm";
import { ErrorDisplay } from "../../components/ErrorDisplay";
import { TarifaSelectionSection } from "../../components/ofertas/TarifaSelectionSection";
import { ContratoSearchSection } from "../../components/ofertas/ContratoSearchSection";
import { DatePickerSection } from "../../components/ofertas/DatePickerSection";
import { TypeOfertaSection } from "../../components/ofertas/TypeOfertaSection";
import OfferWizardWrapper from "../../components/ofertas/OfferWizardWrapper";
import { useOfferWizard } from "../../components/ofertas/OfferWizardContext";

const normalizeSubTypeOffer = (value) =>
  String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const extractSubtypeText = (value) => {
  if (Array.isArray(value)) {
    if (value.length > 1) return String(value[1] || value[0] || "");
    return String(value[0] || "");
  }
  if (value && typeof value === "object") {
    return String(value.name || value.display_name || value.label || value.value || value.id || "");
  }
  return String(value || "");
};

const isCambioTitularSubtype = (value) => {
  const normalized = normalizeSubTypeOffer(value);
  return (
    normalized.startsWith("cambio_titular")
    || normalized.startsWith("cambio_de_titular")
    || (normalized.startsWith("cambio") && normalized.includes("titular"))
  );
};

const isCambioComercializadoraSubtype = (value) => {
  const normalized = normalizeSubTypeOffer(value);
  return (
    normalized.startsWith("cambio_comercializador")
    || normalized.startsWith("cambio_de_comercializador")
    || normalized.startsWith("cambio_comerc")
    || (normalized.startsWith("cambio") && (
      normalized.includes("comercializador") || normalized.includes("comerc")
    ))
  );
};

const requiresOldContractBySubtype = (value) =>
  isCambioTitularSubtype(value) && !isCambioComercializadoraSubtype(value);

const OfertaTarifaForm = ({ user }) => {
  const navigate = useNavigate();
  const params = useParams();
  const oferta_id = params?.oferta_id;
  const sector_type = params.sector_type;
  const tarifasOferta = TARIFAS_OFERTA[sector_type];
  const isRestrictedCallAgent = Boolean(user?.is_call_agent && !user?.is_superadmin);
  const { ofertas_erp, loading: loadingInitialData } = useInitialDataOffer(oferta_id);
  const { setActionHandler, setIsSubmitting } = useOfferWizard();
  const prefillModeValue =
    ofertas_erp?.mode_facturacio ||
    ofertas_erp?.gas_mode_facturacio ||
    "";
  const prefillModeLabel = (() => {
    const rawMode = ofertas_erp?.mode_facturacio || ofertas_erp?.gas_mode_facturacio || "";
    if (rawMode === "atr") return "Fijo";
    if (rawMode === "index") return "Indexada";
    return rawMode || "";
  })();
  const prefillTarifaValue =
    ofertas_erp?.tarifa_oferta?.[0] ||
    ofertas_erp?.tarifa_oferta_gas?.[0] ||
    ofertas_erp?.tarifa_oferta ||
    ofertas_erp?.tarifa_oferta_gas ||
    "";
  const prefillTarifaLabel =
    ofertas_erp?.tarifa_oferta?.[1] ||
    ofertas_erp?.tarifa_oferta_gas?.[1] ||
    ofertas_erp?.tarifa_oferta_desc ||
    ofertas_erp?.tarifa_oferta_gas_desc ||
    "";

  const {
    control,
    handleSubmit,
    setValue,
    errors,
    typeOferta,
    subTypeOffer,
    modeFacturacio,
    showDatePicker,
    onSearchContrato,
    onSubmit,
    submissionError,
    setSubmissionError,
    isSubmitting,
    tarifas,
    loadingTarifas,
    disableTF,
    sub_types_offer_options,
    loadingSubtypes
  } = useOfertaTarifaForm({
    sector_type,
    ofertas_erp,
    oferta_id,
    user
  });

  const requiresContratoSearch =
    !params.oferta_id &&
    (typeOferta === "renovacion" || requiresOldContractBySubtype(subTypeOffer));

  // Sync submitting state with wizard
  useEffect(() => {
    setIsSubmitting(isSubmitting);
  }, [isSubmitting, setIsSubmitting]);

  // Wrap handles with useCallback to stabilize ActionHandler registration
  const handleFormSubmit = useCallback(async (data) => {
    try {
      const result = await onSubmit(data);
      if (!result?.id) {
        setSubmissionError("No se pudo crear la oferta. Intenta nuevamente.");
        return { preventDefaultNavigation: true };
      }
      // Navigation logic
      if (["renovacion_contrato", "renovacion_contrato_gas"].includes(data.sub_type_offer)) {
        navigate("/contratos/ofertas");
      } else {
        navigate(`/${sector_type}/partner/${result.id}`);
      }
      return { preventDefaultNavigation: true };
    } catch (error) {
      console.error("Submission error:", error);
      throw error;
    }
  }, [onSubmit, navigate, sector_type, setSubmissionError]);

  const handleSearchContrato = useCallback(async () => {
    try {
      const contratoRes = await onSearchContrato();
      if (contratoRes) {
        navigate(`/${sector_type}/tarifas/${contratoRes.id}`);
      }
    } catch (error) {
      console.error("Search error:", error);
    }
  }, [navigate, onSearchContrato, sector_type]);

  // Register wizard actions
  useEffect(() => {
    if (requiresContratoSearch) {
      setActionHandler({
        onNext: async () => {
          await handleSearchContrato();
          return { preventDefaultNavigation: true };
        },
        onSave: async () => {
          await handleSearchContrato();
          return { preventDefaultNavigation: true };
        }
      });
      return;
    }

    setActionHandler({
      onNext: () => handleSubmit(handleFormSubmit, (e) => console.error("Validation Error Next:", e))(),
      onSave: () => handleSubmit(async (data) => {
        const result = await onSubmit(data);
        if (!oferta_id && result?.id) {
          navigate(`/${sector_type}/tarifas/${result.id}`, { replace: true });
        }
      }, (e) => console.error("Validation Error Save:", e))()
    });
  }, [setActionHandler, handleSubmit, handleFormSubmit, onSubmit, requiresContratoSearch, handleSearchContrato, oferta_id, navigate, sector_type]);

  const getTarifaReadOnly = () => {
    const subType = extractSubtypeText(subTypeOffer || ofertas_erp?.sub_type_offer);
    if (!subType) return {};

    const normalizedSubType = normalizeSubTypeOffer(subType);
    const isRenewalSubtype = normalizedSubType.startsWith("renovacion");

    if (isRenewalSubtype) {
      const allowsTarifaAcceso =
        normalizedSubType.includes("renovacion_tarifa")
        || normalizedSubType.includes("renovacion_completa")
        || normalizedSubType.includes("_tarifa_")
        || normalizedSubType.endsWith("_tarifa")
        || normalizedSubType.startsWith("tarifa_")
        || normalizedSubType === "tarifa"
        || normalizedSubType.includes("tarifa")
        || normalizedSubType.includes("tarifa_acceso")
        || normalizedSubType.includes("tarifaacceso");

      return {
        // En renovación siempre se puede ajustar tipo de tarifa y tarifas comerciales.
        mode_facturacio: false,
        tarifa_comercial: false,
        // Tarifa de acceso solo se habilita si el subtipo lo indica explícitamente.
        tarifa_oferta: !allowsTarifaAcceso,
        // Coeficiente K editable en renovación (solo se bloquea por tarifa CALL, gestionado aparte).
        coeficiente_k: false,
      };
    }

    return {};
  };

  const tarifaReadOnly = getTarifaReadOnly();

  return (
    <div className="py-4">
      <Container>
        <Row className="justify-content-center">
          <Col lg={6} md={8} xs={12}>
            <Card className="detail-card border-0 shadow-lg rounded-4 overflow-hidden">
              <Card.Header className="bg-white border-bottom-0 pt-4 pb-0 text-center">
                <h2 className="text-primary fw-bold mb-0">Nueva Oferta</h2>
              </Card.Header>
              <Card.Body className="p-4 p-md-5">
                {submissionError && (
                  <ErrorDisplay
                    error={submissionError}
                    onDismiss={() => setSubmissionError(null)}
                  />
                )}

                <Form onSubmit={handleSubmit(handleFormSubmit)}>


                  <TypeOfertaSection
                    control={control}
                    errors={errors}
                    params={params}
                    typeOferta={typeOferta}
                    loadingSubtypes={loadingSubtypes}
                    sub_types_offer_options={sub_types_offer_options}
                    isRestrictedCallAgent={isRestrictedCallAgent}
                  />

                  {requiresContratoSearch ? (
                    <ContratoSearchSection
                      control={control}
                      errors={errors}
                      onSearchContrato={handleSearchContrato}
                    />
                  ) : (
                    <>
                      <TarifaSelectionSection
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        loading={loadingTarifas || loadingInitialData}
                        tarifas={tarifas}
                        disableTF={disableTF}
                        modeFacturacio={modeFacturacio}
                        tarifasOferta={tarifasOferta}
                        sectorType={sector_type}
                        prefillModeValue={prefillModeValue}
                        prefillModeLabel={prefillModeLabel}
                        prefillTarifaValue={prefillTarifaValue}
                        prefillTarifaLabel={prefillTarifaLabel}
                        readOnly={tarifaReadOnly}
                        user={user}
                        isSuperAdmin={Boolean(user?.is_superadmin)}
                      />

                      <CoeficienteKInput
                        control={control}
                        errors={errors}
                        modeFacturacio={modeFacturacio}
                        tarifas={tarifas}
                        setValue={setValue}
                        sector_type={sector_type}
                        readOnly={tarifaReadOnly.coeficiente_k}
                      />

                      <DatePickerSection
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        showDatePicker={showDatePicker}
                      />
                    </>
                  )}
                  {/* ActionButtons removed */}
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

const OfertaTarifa = ({ user }) => {
  const { sector_type, oferta_id } = useParams();
  const viewKey = `${sector_type || "unknown"}-${oferta_id || "new"}`;
  return (
    <Layout
      offerContext={{ sector_type, oferta_id }}
    >
      <OfferWizardWrapper key={viewKey} currentStep="tarifa">
        <OfertaTarifaForm user={user} />
      </OfferWizardWrapper>
    </Layout>
  );
};

const mapStateToProps = (state) => ({
  user: state.Auth.user,
});

export default connect(mapStateToProps)(OfertaTarifa);
